/**
 * Monetary Calculation Utilities - SINGLE SOURCE OF TRUTH (Frontend)
 * 
 * All money calculations in the frontend MUST use these functions
 * to ensure consistency with backend calculations.
 * 
 * IMPORTANT: Money is always stored and displayed with 2 decimal places.
 * Gateway percentages are stored as decimals (0.03 = 3%).
 */

export const DECIMALES = 2;
export const EPSILON = 0.005; // Tolerance for floating point comparison

/**
 * Round a number to 2 decimal places using banker's rounding
 * This prevents cumulative rounding errors in financial calculations
 * 
 * @param {number|string|null} num - The number to round
 * @returns {number} Rounded number with 2 decimals
 */
export function roundTo2(num) {
  if (num === null || num === undefined || num === '') return 0;
  const parsed = parseFloat(num);
  if (isNaN(parsed)) return 0;
  
  // Use Number.EPSILON to handle floating point precision issues
  return Math.round((parsed + Number.EPSILON) * 100) / 100;
}

/**
 * Safe sum of monetary values
 * Handles null/undefined values and ensures 2 decimal precision
 * 
 * @param {...number} montos - Variable number of amounts to sum
 * @returns {number} Sum rounded to 2 decimals
 */
export function sumarMontos(...montos) {
  const suma = montos.reduce((acc, m) => {
    const parsed = parseFloat(m);
    return acc + (isNaN(parsed) ? 0 : parsed);
  }, 0);
  return roundTo2(suma);
}

/**
 * Safe subtraction of monetary values
 * 
 * @param {number} minuendo - The number to subtract from
 * @param {number} sustraendo - The number to subtract
 * @returns {number} Result rounded to 2 decimals
 */
export function restarMontos(minuendo, sustraendo) {
  const a = parseFloat(minuendo) || 0;
  const b = parseFloat(sustraendo) || 0;
  return roundTo2(a - b);
}

/**
 * Safe multiplication of monetary values
 * 
 * @param {number} monto - The amount
 * @param {number} factor - The multiplier (e.g., quantity)
 * @returns {number} Result rounded to 2 decimals
 */
export function multiplicarMonto(monto, factor) {
  const a = parseFloat(monto) || 0;
  const b = parseFloat(factor) || 0;
  return roundTo2(a * b);
}

/**
 * Calculate gateway commission
 * 
 * IMPORTANT: porcentajeComision is stored as decimal (0.03 = 3%)
 * Formula: (monto * porcentaje) + montoFijo
 * 
 * @param {number} monto - Payment amount
 * @param {number} porcentajeComision - Decimal percentage (0.03 = 3%)
 * @param {number} montoFijoComision - Fixed commission amount (default: 0)
 * @returns {number} Commission amount rounded to 2 decimals
 */
export function calcularComisionPasarela(monto, porcentajeComision, montoFijoComision = 0) {
  const montoNum = parseFloat(monto) || 0;
  const porcentaje = parseFloat(porcentajeComision) || 0;
  const fijo = parseFloat(montoFijoComision) || 0;
  
  // Validate percentage is in decimal format (0-1)
  if (porcentaje > 1) {
    console.warn(`calcularComisionPasarela: porcentaje ${porcentaje} parece estar en formato %, se esperaba decimal`);
  }
  
  const comision = (montoNum * porcentaje) + fijo;
  return roundTo2(comision);
}

/**
 * Calculate ITBIS (tax) for an amount
 * 
 * @param {number} montoBase - Base amount before tax
 * @param {number} tasaITBIS - Tax rate as percentage (18, 16, 0)
 * @returns {number} Tax amount rounded to 2 decimals
 */
export function calcularITBIS(montoBase, tasaITBIS) {
  const base = parseFloat(montoBase) || 0;
  const tasa = parseFloat(tasaITBIS) || 0;
  
  // Convert percentage to decimal (18 -> 0.18)
  const tasaDecimal = tasa / 100;
  return roundTo2(base * tasaDecimal);
}

/**
 * Validate that a balance calculation is correct
 * Returns true if monto_total - totalPagado = balanceActual (within epsilon)
 * 
 * @param {number} montoTotal - Total invoice amount
 * @param {number} totalPagado - Sum of all payments
 * @param {number} balanceActual - Current balance
 * @returns {boolean} True if balance is correct
 */
export function validarBalance(montoTotal, totalPagado, balanceActual) {
  const esperado = roundTo2(parseFloat(montoTotal) - parseFloat(totalPagado));
  const actual = roundTo2(balanceActual);
  return Math.abs(esperado - actual) < EPSILON;
}

/**
 * Calculate new balance after a payment
 * 
 * @param {number} balanceActual - Current balance
 * @param {number} montoPago - Payment amount
 * @returns {number} New balance (cannot be negative)
 */
export function calcularNuevoBalance(balanceActual, montoPago) {
  const balance = parseFloat(balanceActual) || 0;
  const pago = parseFloat(montoPago) || 0;
  const nuevo = roundTo2(balance - pago);
  
  // Balance cannot be negative
  return nuevo < 0 ? 0 : nuevo;
}

/**
 * Validate that a payment amount is valid for the given balance
 * 
 * @param {number} montoPago - Payment amount to validate
 * @param {number} balanceActual - Current invoice balance
 * @returns {{ valido: boolean, mensaje: string|null }} Validation result
 */
export function validarMontoPago(montoPago, balanceActual) {
  const pago = parseFloat(montoPago);
  const balance = parseFloat(balanceActual);
  
  if (isNaN(pago) || pago <= 0) {
    return { valido: false, mensaje: 'El monto del pago debe ser mayor a 0' };
  }
  
  if (isNaN(balance) || balance <= 0) {
    return { valido: false, mensaje: 'La factura no tiene balance pendiente' };
  }
  
  if (pago > balance + EPSILON) {
    return { 
      valido: false, 
      mensaje: `El monto (${roundTo2(pago).toFixed(2)}) excede el balance pendiente (${roundTo2(balance).toFixed(2)})` 
    };
  }
  
  return { valido: true, mensaje: null };
}

/**
 * Format a number as monetary string (2 decimals)
 * 
 * @param {number} monto - Amount to format
 * @returns {string} Formatted string with 2 decimals
 */
export function formatearMonto(monto) {
  return roundTo2(monto).toFixed(2);
}

/**
 * Format a number as currency with locale formatting
 * 
 * @param {number} monto - Amount to format
 * @param {string} locale - Locale string (default: 'es-DO')
 * @returns {string} Formatted currency string
 */
export function formatearMoneda(monto, locale = 'es-DO') {
  return roundTo2(monto).toLocaleString(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
}

/**
 * Parse a monetary string to number
 * Handles various formats (with commas, currency symbols, etc.)
 * 
 * @param {string|number} valor - Value to parse
 * @returns {number} Parsed number rounded to 2 decimals
 */
export function parsearMonto(valor) {
  if (typeof valor === 'number') return roundTo2(valor);
  if (!valor) return 0;
  
  // Remove currency symbols, commas (thousands separator), spaces
  const cleaned = String(valor)
    .replace(/[RD$\s]/g, '')
    .replace(/,/g, '');
  
  return roundTo2(parseFloat(cleaned));
}

/**
 * Convert gateway percentage from decimal to display percentage
 * e.g., 0.03 -> 3.00
 * 
 * @param {number} decimal - Decimal percentage (0.03)
 * @returns {number} Display percentage (3.00)
 */
export function porcentajeDecimalADisplay(decimal) {
  return roundTo2((parseFloat(decimal) || 0) * 100);
}

/**
 * Convert display percentage to decimal for storage
 * e.g., 3 -> 0.03
 * 
 * @param {number} porcentaje - Display percentage (3)
 * @returns {number} Decimal percentage (0.03)
 */
export function porcentajeDisplayADecimal(porcentaje) {
  const p = parseFloat(porcentaje) || 0;
  // Round to 4 decimals for storage (e.g., 0.0325)
  return Math.round((p / 100) * 10000) / 10000;
}



